import React from 'react';
import { getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { emptyArray } from '@fabric-ui/utils';
import { Theme } from '@mui/material';
import { Bar, Cell, Dot, LabelListProps, Line } from 'recharts';
import { isPercentageMetric } from '../../utils/dashboardDataUtils';
import { isExportMode } from '../../utils/exportDashboards.utils';
import { isNullOrUndefined } from '../../utils/utils';
import { isSelectedDot } from './utils';

export const componentType = {
  bar: ({
    props,
    onSelect,
    selected = [],
    data,
    hoveredCell = {},
    setHoveredCell = () => {},
    getFormattedColorCell,
    renderLabelList,
    enableDefaultMinPointSize = false,
  }: {
    props: any;
    onSelect?: (
      cell: { d: unknown; dataKey: string; widgetData: typeof data },
      event?: Parameters<NonNullable<React.ComponentProps<typeof Bar>['onClick']>>[2]
    ) => undefined;
    selected: any[];
    data;
    hoveredCell;
    getFormattedColorCell?: (v: number, metric: string) => string | undefined;
    setHoveredCell: (arg: { name: string; dataKey: any; d: unknown } | undefined) => void;
    renderLabelList: (props?: LabelListProps<typeof data>) => React.ReactNode;
    enableDefaultMinPointSize?: boolean;
  }) => {
    const { dataKey } = props;
    const propsMinPointSize = Number(props.minPointSize);
    const minPointSize = !Number.isNaN(propsMinPointSize) ? propsMinPointSize : dataKey && enableDefaultMinPointSize ? 3 : 0;

    return (
      <Bar
        yAxisId={props.yAxisId || isPercentageMetric(dataKey) || props.isRightAxis ? 'right' : 'left'}
        key={dataKey}
        name={dataKey || props.name}
        dataKey={dataKey}
        barSize={props.barSize || 20}
        fill={props.color}
        onClick={(d, _index, event) => {
          onSelect?.({ d, dataKey: props.dataKey, widgetData: data }, event);
          event.stopPropagation();
        }}
        hide={props.hide}
        {...(minPointSize ? { minPointSize } : {})}
        isAnimationActive={!isExportMode() && props.animation}
        label={props.label}
        {...(props.stackId && { stackId: props.stackId })}>
        {props?.children ||
          (props.color !== 'transparent' &&
            Array.isArray(data) &&
            data.map((entry, i, arr) => {
              const numericValue = Number(entry[dataKey]);
              const display = Number.isNaN(numericValue) || numericValue === 0 ? 'none' : undefined;
              return (
                <>
                  {arr.length - 1 === i && renderLabelList ? renderLabelList() : null}

                  <Cell
                    key={entry.name}
                    onMouseEnter={e => {
                      e.stopPropagation();
                      if (onSelect) {
                        setHoveredCell({ name: entry.name, dataKey, d: entry });
                      }
                    }}
                    onMouseLeave={e => {
                      e.stopPropagation();
                      setHoveredCell(undefined);
                    }}
                    onBlur={e => {
                      e.stopPropagation();
                      setHoveredCell(undefined);
                    }}
                    cursor={onSelect ? 'pointer' : 'default'}
                    filter={
                      hoveredCell.name === entry.name && hoveredCell.dataKey === dataKey
                        ? `drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.60)`
                        : undefined
                    }
                    fill={getCssVarColorWithOpacity(
                      getFormattedColorCell?.(entry[dataKey], dataKey) || entry.color || props.color,
                      selected.some(v => v.name === entry.name && (!v.dataKey || v.dataKey === dataKey)) || selected.length === 0 ? 1 : 0.15
                    )}
                    role="img"
                    aria-label={entry.name}
                    {...(display && { display })}
                  />
                </>
              );
            }))}
      </Bar>
    );
  },
  line: ({
    props,
    theme,
    renderLabelList,
    selected = emptyArray,
  }: {
    props: any;
    theme: Theme;
    renderLabelList: (props?: LabelListProps<{}>) => React.ReactNode;
    selected;
  }) => (
    <Line
      yAxisId={props.yAxisId || isPercentageMetric(props.dataKey) || props.isRightAxis ? 'right' : 'left'}
      type={props.lineType || 'monotone'}
      key={props.dataKey}
      name={props.name || props.dataKey}
      dataKey={props.dataKey}
      strokeWidth={3}
      dot={dotProps => {
        const customProps = isSelectedDot(selected, dotProps)
          ? { fill: theme.palette.white.main, stroke: props.color, strokeWidth: 3, r: 7 }
          : !isNullOrUndefined(dotProps.dot)
            ? dotProps.dot
            : { r: 0 };

        return <Dot {...dotProps} {...customProps} data-testid={`line-chart-dot-${dotProps.payload.date}`} />;
      }}
      isAnimationActive={!isExportMode() && props.animation}
      hide={props.hide}
      activeDot={dotProps => {
        const customProps = isSelectedDot(selected, dotProps)
          ? { fill: theme.palette.white.main, stroke: props.color, strokeWidth: 3, r: 7 }
          : isNullOrUndefined(props.activeDot)
            ? props.activeDot
            : { fill: theme.palette.white.main, stroke: props.color, strokeWidth: 3, r: 4 };

        return (
          <Dot
            {...dotProps}
            {...customProps}
            fill={theme.palette.white.main}
            stroke={props.color}
            data-line-active-dot-index={dotProps.index}
          />
        );
      }}
      stroke={props.color}
      data-testid={`line-chart-line-${props.dataKey}`}>
      {renderLabelList
        ? renderLabelList({
            fill: props.color,
          })
        : null}
    </Line>
  ),
};
